import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, Send, Linkedin, Twitter, Instagram, Facebook } from "lucide-react";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim() || "General Enquiry",
        message: form.message.trim(),
      });
      toast.success("Thank you! Our team will be in touch within 24 business hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail
        ? Array.isArray(err.response.data.detail)
          ? err.response.data.detail[0]?.msg
          : err.response.data.detail
        : "Something went wrong. Please try again or call us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section relative bg-[#080808]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37]">Get in Touch</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white mt-4 leading-[1.05]">
            Let's design your
            <span className="text-gold-gradient"> wealth roadmap.</span>
          </h2>
          <p className="mt-5 text-gray-400 max-w-2xl mx-auto">
            Share a few details and a member of our team will reach out for a no-obligation introductory discussion.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-3 glass rounded-2xl p-7 lg:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name" name="name" value={form.name} onChange={handleChange} required testId="contact-name-input" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required testId="contact-email-input" />
              <Field label="Phone" name="phone" value={form.phone} onChange={handleChange} required testId="contact-phone-input" />
              <Field label="Subject" name="subject" value={form.subject} onChange={handleChange} placeholder="e.g. SIP Planning" testId="contact-subject-input" />
            </div>
            <div className="mt-5">
              <label className="text-xs uppercase tracking-[0.25em] text-gray-400">Message</label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                minLength={5}
                rows={5}
                data-testid="contact-message-input"
                placeholder="Tell us about your investment goals..."
                className="mt-2 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:border-[#D4AF37]/60 focus:outline-none transition resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="mt-6 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#D4AF37] text-black font-semibold hover:bg-[#FFD700] disabled:opacity-60 disabled:cursor-not-allowed transition shadow-[0_15px_45px_-10px_rgba(212,175,55,0.55)]"
            >
              {submitting ? "Submitting..." : (<>Send Message <Send size={16} /></>)}
            </button>
            <p className="mt-4 text-xs text-gray-500 leading-relaxed">
              By submitting this form, you agree to our <a href="/legal/privacy-policy" className="text-[#D4AF37] hover:underline">Privacy Policy</a>.
              Mutual fund investments are subject to market risks.
            </p>
          </motion.form>

          {/* Info + Map */}
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-2 flex flex-col gap-5"
          >
            <div className="glass rounded-2xl p-7 space-y-5">
              <div className="text-[12px] leading-snug text-gray-400 pb-3 border-b border-white/10" data-testid="contact-amfi-tagline">
                AMFI Registered Mutual Fund Distributor | ARN-365843
              </div>
              <InfoRow icon={<Phone size={18} />} label="Call us" value={SITE.phone} href={`tel:${SITE.phoneRaw}`} />
              <InfoRow icon={<Mail size={18} />} label="Email" value={SITE.email} href={`mailto:${SITE.email}`} />
              <InfoRow icon={<MapPin size={18} />} label="Office" value={SITE.address} />

              <div className="pt-4 border-t border-white/10">
                <div className="text-xs uppercase tracking-[0.25em] text-gray-400 mb-3">Follow us</div>
                <div className="flex gap-2">
                  {[
                    { icon: Linkedin, href: SITE.socials.linkedin, label: "LinkedIn" },
                    { icon: Twitter, href: SITE.socials.twitter, label: "Twitter" },
                    { icon: Instagram, href: SITE.socials.instagram, label: "Instagram" },
                    { icon: Facebook, href: SITE.socials.facebook, label: "Facebook" },
                  ].map(({ icon: Icon, href, label }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={label}
                      data-testid={`social-${label.toLowerCase()}`}
                      className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-300 hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37] transition"
                    >
                      <Icon size={16} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-white/10 h-64" data-testid="contact-map">
              <iframe
                src={SITE.mapEmbedSrc}
                title="Axomind office map"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.4)" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", required, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.25em] text-gray-400">{label}{required && " *"}</label>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-2 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:border-[#D4AF37]/60 focus:outline-none transition"
      />
    </div>
  );
}

function InfoRow({ icon, label, value, href }) {
  const Inner = (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-gray-500">{label}</div>
        <div className="text-white mt-1 font-medium">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:opacity-90 transition">{Inner}</a> : Inner;
}
